import { useState, useEffect, useRef } from 'react';
import { Dialog } from '@headlessui/react';
import { XMarkIcon, PaperAirplaneIcon, KeyIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Message, GroupOption, IndividualOption } from './types';

interface SecureChatProps {
  isOpen: boolean;
  onClose: () => void;
}

const groupOptions: GroupOption[] = [
  { id: 'ccet', name: 'Cyber Crisis Executive Team (CCET)' },
  { id: 'ccmt', name: 'Cyber Crisis Management Team (CCMT)' },
  { id: 'ccrt', name: 'Cyber Crisis Response Team (CCRT)' },
  { id: 'ccst', name: 'Cyber Crisis Support Team (CCST)' },
  { id: 'csirt', name: 'Computer Security Incident Response Team (CSIRT)' },
  { id: 'broadcast', name: 'BROADCAST - ALL' },
];

const individualOptions: IndividualOption[] = [
  { id: 'seic', name: 'Senior Executive-in-Charge (SEIC)' },
  { id: 'eic', name: 'Executive-in-Charge (EIC)' },
  { id: 'lih', name: 'Lead Incident Handler (LIH)' },
  { id: 'irl-legal', name: 'Incident Response Lead - Legal' },
  { id: 'irl-support', name: 'Incident Response Lead - Customer Support' },
  { id: 'irl-ops', name: 'Incident Response Lead - Operations' },
];

export default function SecureChat({ isOpen, onClose }: SecureChatProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [selectedIndividual, setSelectedIndividual] = useState<string>('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    const recipient = selectedIndividual || selectedGroup;
    if (!recipient) {
      alert('Please select a recipient');
      return;
    }

    // In production, this would be encrypted before sending
    const message: Message = {
      id: Date.now().toString(),
      content: newMessage,
      sender: 'Current User', // In production, this would be the actual user
      senderRole: 'EIC', // In production, this would be the actual role
      timestamp: new Date().toISOString(),
      recipient,
      type: selectedIndividual ? 'individual' : 'group',
    };

    setMessages([...messages, message]);
    setNewMessage('');
  };

  const handleKeyUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const key = e.target?.result as string;
        setEncryptionKey(key);
      };
      reader.readAsText(file);
    }
  };

  return (
    <Dialog
      as="div"
      className="fixed inset-0 z-50 overflow-y-auto"
      open={isOpen}
      onClose={onClose}
    >
      <div className="min-h-screen px-4 text-center">
        <Dialog.Overlay className="fixed inset-0 bg-black opacity-30" />
        
        <div className="inline-block w-full max-w-4xl my-8 text-left align-middle transition-all transform bg-white shadow-xl rounded-lg">
          <div className="border-b border-gray-200 p-4 flex justify-between items-center">
            <Dialog.Title as="h3" className="text-lg font-medium text-gray-900">
              Secure Chat
            </Dialog.Title>
            <button
              onClick={onClose}
              className="rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <div className="p-4 space-y-4">
            {/* Recipient Selection */}
            <div className="flex space-x-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Select Group
                </label>
                <select
                  value={selectedGroup}
                  onChange={(e) => {
                    setSelectedGroup(e.target.value);
                    setSelectedIndividual('');
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  disabled={!!selectedIndividual}
                >
                  <option value="">Select a group...</option>
                  {groupOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">
                  Select Individual
                </label>
                <select
                  value={selectedIndividual}
                  onChange={(e) => {
                    setSelectedIndividual(e.target.value);
                    setSelectedGroup('');
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  disabled={!!selectedGroup}
                >
                  <option value="">Select an individual...</option>
                  {individualOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Messages Area */}
            <div className="h-[400px] overflow-y-auto border rounded-lg p-4 space-y-4 bg-gray-50">
              {messages.map((message) => (
                <div
                  key={message.id}
                  className={clsx(
                    'flex flex-col max-w-[70%] space-y-1 p-3 rounded-lg',
                    message.sender === 'Current User'
                      ? 'ml-auto bg-indigo-100'
                      : 'bg-white border'
                  )}
                >
                  <div className="flex items-baseline justify-between">
                    <span className="font-medium text-sm">
                      {message.sender} ({message.senderRole})
                    </span>
                    <span className="text-xs text-gray-500">
                      {new Date(message.timestamp).toLocaleString()}
                    </span>
                  </div>
                  <p className="text-sm">{message.content}</p>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Message Input */}
            <form onSubmit={handleSendMessage} className="flex space-x-4">
              <input
                type="text"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                placeholder="Type your message..."
                className="flex-1 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <PaperAirplaneIcon className="h-5 w-5 mr-2" />
                Send
              </button>
            </form>
          </div>
        </div>
      </div>
    </Dialog>
  );
}